import React from "react";
import {
  WishListDescription,
  WishListMedia,
  WishListProduct,
  WSProductLabel,
  WSProductRemoveLabel,
  WSProductTitle,
} from "./styles";
import ImageComponent from "next/image";
import SelectDropdownComponent from "@/components/Contacts/Form/SelectDropdown";
import {
  wishlistLineRemove,
  wishlistLineUpdate,
} from "@/lib/redux/wishlist/wishlistSlice";
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks";
import LinkComponent from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "@/lib/translations";

// Cast to fix React 17 + @types/react 18 compatibility issue
const Link = LinkComponent as any;
const Image = ImageComponent as any;
const SelectDropdown = SelectDropdownComponent as any;

type Product = {
  _id: string;
  title?: string;
  thumbnail?: {
    asset?: {
      url?: string;
      metadata?: {
        dimensions?: {
          width: number;
          height: number;
        };
      };
      alt?: string;
    };
  };
  application: {
    name: string;
    values?: string[];
  };
};

type Props = {
  products?: Product[];
};

const List: React.FC<Props> = ({ products }) => {
  const { locale = "it" } = useRouter();

  const t = {
    removeProduct: useTranslation("wishlist.removeProduct", locale) || "",
    selectQuantity: useTranslation("wishlist.selectQuantity", locale) || "",
    quantity1: useTranslation("wishlist.quantity1", locale) || "",
    quantity2: useTranslation("wishlist.quantity2", locale) || "",
    quantity3: useTranslation("wishlist.quantity3", locale) || "",
    quantity4: useTranslation("wishlist.quantity4", locale) || "",
    quantity5: useTranslation("wishlist.quantity5", locale) || "",
    quantity6: useTranslation("wishlist.quantity6", locale) || "",
    quantity7: useTranslation("wishlist.quantity7", locale) || "",
  };

  const OPTIONS = [
    { value: t.quantity1, label: t.quantity1 },
    { value: t.quantity2, label: t.quantity2 },
    { value: t.quantity3, label: t.quantity3 },
    { value: t.quantity4, label: t.quantity4 },
    { value: t.quantity5, label: t.quantity5 },
    { value: t.quantity6, label: t.quantity6 },
    { value: t.quantity7, label: t.quantity7 },
  ];

  const dispatch = useAppDispatch();
  const wishlist = useAppSelector((state) => state.wishlist);

  const removeLine = (id: string) => {
    dispatch(wishlistLineRemove(id));
  };

  const updateLine = (id: string, value: string) => {
    dispatch(wishlistLineUpdate({ productID: id, quantity: value }));
  };

  const getDefaultValue = (id: string) => {
    const target = wishlist.lines.find((list: any) => list.productID === id);
    return {
      value: t.selectQuantity,
      label: t.selectQuantity,
    };
  };

  const getProducts = () => {
    return products?.map((product: any) => (
      <WishListProduct
        className="wish-list-product"
        key={product._id + "wish-list-product"}
      >
        <Link href={"/products/" + product._id} passHref>
          <WishListMedia>
            {product.thumbnail?.asset && product.thumbnail?.asset?.url && (
              <Image
                src={product.thumbnail?.asset?.url}
                layout="responsive"
                alt={product.thumbnail?.alt || ""}
                width={product.thumbnail?.asset?.metadata?.dimensions?.width}
                height={product.thumbnail?.asset?.metadata?.dimensions?.height}
                priority
              />
            )}
          </WishListMedia>
        </Link>
        <WishListDescription>
          <Link href={"/products/" + product._id} passHref>
            <WSProductTitle>{product?.title}</WSProductTitle>
          </Link>
          <WSProductLabel>
            {product.application.values.join(", ")}
          </WSProductLabel>
          <SelectDropdown
            key={product._id}
            placeholder={t.selectQuantity}
            items={OPTIONS}
            overrideStyles={customStyles}
            defaultValue={getDefaultValue(product._id)}
            itemId={product._id}
            callBackFn={updateLine}
          />
          <WSProductRemoveLabel onClick={() => removeLine(product._id)}>
            {t.removeProduct}
          </WSProductRemoveLabel>
        </WishListDescription>
      </WishListProduct>
    ));
  };

  return <>{getProducts()}</>;
};

const customStyles: any = {
  valueContainer: () => ({
    fontSize: "0.875rem",
    paddingLeft: "2px",
    lineHeight: "normal",
  }),
};

export default List;
